// src/routes/JobPostCreation/analysisFieldMapper.js

/**
 * 分析結果をフィールドにマッピングするユーティリティ
 * バックエンドの fieldMapping を使用して、各フィールドに関連する分析結果を取得
 */

// スコア関連ユーティリティを共通モジュールから再エクスポート
export { getScoreBorderColor, getScoreBackgroundColor, getScoreStatusLabel, getScoreIcon } from '../../utils/scoreUtils';

// 分析軸の日本語ラベル
const ANALYSIS_LABELS = {
  titleAnalysis: 'タイトル',
  lengthAnalysis: '文字数',
  readabilityAnalysis: '読みやすさ',
  inclusiveAnalysis: '表現チェック',
  salaryAnalysis: '給与情報',
  benefitsAnalysis: '福利厚生',
  workArrangementAnalysis: '働き方',
  requirementInflationAnalysis: '応募条件',
  specificityAnalysis: '具体性',
  careerGrowthAnalysis: '成長・キャリア',
  companyCultureAnalysis: '会社の雰囲気',
  applicationProcessAnalysis: '選考の流れ',
};

/**
 * 分析結果からスコアを取得（各分析軸のスコアフィールド名が異なる場合に対応）
 */
function getAnalysisScore(analysisData) {
  if (!analysisData) return null;
  if (typeof analysisData.score === 'number') return analysisData.score;
  if (typeof analysisData.transparencyScore === 'number') return analysisData.transparencyScore;
  return null;
}

/**
 * 指定フィールドに関連する分析結果のリストを取得
 * @param {Object} analysis - バックエンドからの分析結果全体
 * @param {string} fieldName - フィールド名（title/summary/jobDescription/targetPerson/companyPoints/overall）
 * @returns {Array<{key: string, label: string, score: number, data: Object}>}
 */
export function getFieldAnalyses(analysis, fieldName) {
  if (!analysis || !analysis.fieldMapping) return [];

  const analysisKeys = analysis.fieldMapping[fieldName] || [];

  return analysisKeys
    .map(key => {
      const data = analysis[key];
      if (!data) return null;
      const score = getAnalysisScore(data);
      return {
        key,
        label: ANALYSIS_LABELS[key] || key,
        score: score !== null ? score : 0,
        data,
      };
    })
    .filter(Boolean);
}

/**
 * フィールドの平均スコアを取得
 * @param {Object} analysis - 分析結果全体
 * @param {string} fieldName - フィールド名
 * @returns {number|null} 平均スコア（分析なしの場合null）
 */
export function getFieldScore(analysis, fieldName) {
  const analyses = getFieldAnalyses(analysis, fieldName);
  if (analyses.length === 0) return null;
  const total = analyses.reduce((sum, a) => sum + a.score, 0);
  return Math.round(total / analyses.length);
}



// ============================================================
// AI品質チェック（GPT深層分析）用のマッピング
// ============================================================

// AI品質チェックでも同じ軸名を使用（ルールベースと統一）
const AI_ANALYSIS_LABELS = {
  ...ANALYSIS_LABELS,
  // 旧AI独自軸名のフォールバック
  clarity: '役割の明確さ',
  culture: '企業文化の信憑性',
  tone: 'トーンの適切さ',
  consistency: '論理的一貫性',
  persuasiveness: '訴求力',
  writing: '文章品質',
  completeness: '情報の網羅性',
};

/**
 * AI分析結果から指定フィールドに関連する項目を取得
 * @param {Object} aiAnalysis - AIQualityCheckResult
 * @param {string} fieldName - フィールド名（title/summary/jobDescription/targetPerson/companyPoints/overall）
 * @returns {Array<{key: string, label: string, score: number, assessment: string, suggestions: Array}>}
 */
export function getFieldAiAnalyses(aiAnalysis, fieldName) {
  if (!aiAnalysis || !aiAnalysis.items) return [];

  return aiAnalysis.items
    .filter(item => item.fieldRelevance && item.fieldRelevance.includes(fieldName))
    .map(item => ({
      key: item.axis,
      label: AI_ANALYSIS_LABELS[item.axis] || item.label || item.axis,
      score: item.score || 0,
      assessment: item.assessment || '',
      suggestions: item.suggestions || [],
    }));
}
